import React, { useState } from "react";
import { Instagram, MessageCircle, MapPin } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { t, tr } from "../../i18n/translations";
import { toast } from "sonner";

export default function Contact() {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(tr(t.contact.success, lang));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#FAFAFA] py-28 sm:py-36" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="overline text-[#8B6D5E] mb-4">{tr(t.contact.overline, lang)}</p>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1] mb-8">
              {tr(t.contact.title, lang)}
            </h2>
            <p className="text-base text-[#5C5C5C] leading-relaxed mb-10 max-w-md">{tr(t.contact.sub, lang)}</p>

            <div className="space-y-5">
              <a
                href="https://wa.me/message/SG2ELRTG32LVF1"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-lg text-[#1A1A1A] link-underline w-fit"
                data-testid="contact-whatsapp"
              >
                <MessageCircle size={18} /> {tr(t.contact.whatsapp, lang)}
              </a>
              <a
                href="https://www.instagram.com/abrilsuamkp/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-lg text-[#1A1A1A] link-underline w-fit"
                data-testid="contact-instagram"
              >
                <Instagram size={18} /> @abrilsuamkp
              </a>
              <p className="flex items-center gap-3 text-lg text-[#1A1A1A]">
                <MapPin size={18} /> {tr(t.contact.location, lang)}
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="contact-form">
            <div className="sm:col-span-1">
              <label className="field-label">{tr(t.contact.name, lang)}</label>
              <input
                required
                className="field"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="field-label">{tr(t.contact.email, lang)}</label>
              <input
                required
                type="email"
                className="field"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">{tr(t.contact.message, lang)}</label>
              <textarea
                required
                className="field"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
              />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={loading} className="btn-primary" data-testid="contact-submit">
                {loading ? "…" : tr(t.contact.send, lang)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
